import type { CrosswordData } from '../types'
import { sanitizeCrosswordData } from './data'

const SYNC_KEY_STORAGE = 'crossword-sync-key'
const CLIENT_ID_STORAGE = 'crossword-client-id'

export type SyncStatus = 'idle' | 'syncing' | 'synced' | 'error'

// Generate or retrieve a persistent client ID for this browser
export function getClientId(): string {
  let clientId = localStorage.getItem(CLIENT_ID_STORAGE)
  if (!clientId) {
    clientId = crypto.randomUUID()
    localStorage.setItem(CLIENT_ID_STORAGE, clientId)
  }
  return clientId
}

export function getSyncKeyFromUrl(): string | null {
  const params = new URLSearchParams(window.location.search)
  return params.get('key')
}

export function getSyncKey(): string | null {
  // URL takes precedence
  const urlKey = getSyncKeyFromUrl()
  if (urlKey) {
    // Also save to localStorage for persistence
    localStorage.setItem(SYNC_KEY_STORAGE, urlKey)
    return urlKey
  }
  return localStorage.getItem(SYNC_KEY_STORAGE)
}

export function setSyncKey(key: string | null): void {
  if (key) {
    localStorage.setItem(SYNC_KEY_STORAGE, key)
  } else {
    localStorage.removeItem(SYNC_KEY_STORAGE)
  }
}

export function clearSyncKey(): void {
  localStorage.removeItem(SYNC_KEY_STORAGE)
  // Remove from URL if present
  const url = new URL(window.location.href)
  url.searchParams.delete('key')
  window.history.replaceState({}, '', url.toString())
}

export function getSyncUrl(key: string): string {
  const url = new URL(window.location.href)
  url.searchParams.set('key', key)
  return url.toString()
}

type FetchResult =
  | { success: true; data: CrosswordData | null; updatedAt: string | null; clientId: string | null }
  | { success: false; error: string }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export async function fetchSyncedData(key: string): Promise<FetchResult> {
  const response = await fetch(`/api/sync/${encodeURIComponent(key)}`)

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    return { success: false, error: error.error ?? 'Fetch failed' }
  }

  const result: unknown = await response.json()
  if (!isRecord(result)) {
    return { success: false, error: 'Invalid sync response' }
  }

  const rawData = Object.hasOwn(result, 'data') ? result['data'] : result
  const updatedAt = typeof result['updatedAt'] === 'string' ? result['updatedAt'] : null
  const clientId = typeof result['clientId'] === 'string' ? result['clientId'] : null

  if (rawData == null) {
    return { success: true, data: null, updatedAt, clientId }
  }

  const data = sanitizeCrosswordData(rawData)
  if (!data) {
    return { success: false, error: 'Remote data is not a valid crossword file' }
  }

  return { success: true, data, updatedAt, clientId }
}

type SaveResult =
  | { success: true; updatedAt: string }
  | { success: false; error: string }

export async function saveSyncedData(key: string, data: CrosswordData): Promise<SaveResult> {
  const clientId = getClientId()
  const response = await fetch(`/api/sync/${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ data, clientId }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }))
    return { success: false, error: error.error ?? 'Save failed' }
  }

  const result: unknown = await response.json().catch(() => ({}))
  const updatedAt = isRecord(result) && typeof result['updatedAt'] === 'string'
    ? result['updatedAt']
    : new Date().toISOString()

  return { success: true, updatedAt }
}

type WebSocketMessage =
  | { type: 'connected'; clientId: string }
  | { type: 'updated' }

export function connectWebSocket(
  key: string,
  onUpdate: () => void,
  onConnected?: () => void
): { close: () => void } {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const clientId = getClientId()
  // Pass clientId as query param since WebSocket API doesn't support custom headers
  const wsUrl = `${protocol}//${window.location.host}/ws/sync/${encodeURIComponent(key)}?clientId=${encodeURIComponent(clientId)}`

  let ws: WebSocket | null = null
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  let pingInterval: ReturnType<typeof setInterval> | null = null
  let closed = false

  function connect() {
    if (closed) return

    ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      // Start ping interval to keep connection alive
      pingInterval = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send('ping')
        }
      }, 30000)
    }

    ws.onmessage = (event) => {
      if (event.data === 'pong') return

      const message: WebSocketMessage = JSON.parse(event.data)

      if (message.type === 'connected') {
        onConnected?.()
      } else if (message.type === 'updated') {
        onUpdate()
      }
    }

    ws.onclose = () => {
      if (pingInterval) {
        clearInterval(pingInterval)
        pingInterval = null
      }

      // Reconnect after delay
      if (!closed) {
        reconnectTimeout = setTimeout(connect, 2000)
      }
    }

    ws.onerror = () => {
      ws?.close()
    }
  }

  connect()

  return {
    close: () => {
      closed = true
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
      }
      if (pingInterval) {
        clearInterval(pingInterval)
      }
      ws?.close()
    },
  }
}
